import { useEffect } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Video, Heart, Sparkles, Calendar, MapPin, Users } from "lucide-react";

import { fetchEventBySlug } from "@/lib/api/events";
import {
  fetchMyParticipantForEvent,
  fetchParticipantById,
} from "@/lib/api/participants";
import {
  clearParticipantSession,
  getParticipantSession,
  saveParticipantSession,
} from "@/lib/participant-session";
import { useAuthUser } from "@/lib/use-auth-user";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Loader, EmptyState } from "@/components/ui/States";
import { MobileFrame } from "@/components/ui/MobileFrame";

function formatEventDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

const ONBOARDING_STEPS = [
  {
    icon: Video,
    title: "Record a 60-second pitch",
    desc: "Who you are, what you're looking for, what you can help with. AI builds your profile instantly.",
  },
  {
    icon: Heart,
    title: "Like people you want to meet",
    desc: "If they like you back — it's a match. Contacts are shared only when both sides say yes.",
  },
  {
    icon: Sparkles,
    title: "Matches open before the event",
    desc: "A couple of hours before the start, your matches reveal — names, contacts, and a reason to talk.",
  },
] as const;

/**
 * Decide where to send a returning, already-registered participant. The
 * goal is "land where you can act":
 *   - registration incomplete (no contact) → /continue
 *   - profile incomplete (no looking_for/can_help_with) → /goals
 *   - profile complete, no pitch → /feed (TZ block A: video is no
 *     longer a hard gate; the feed opens with a text card and a
 *     "Record pitch" banner)
 *   - profile complete, video uploaded → /feed
 *
 * The previous version forced anyone without a video onto /profile,
 * which TZ block A explicitly reverses — the participant now lands
 * where they can act and the feed itself nudges them to record.
 */
function pickReturnTarget(p: {
  contactForMatches: string | null | undefined;
  lookingFor: string | null | undefined;
  canHelpWith: string | null | undefined;
}, slug: string): string {
  if (!p.contactForMatches) return `/e/${slug}/continue`;
  const profileComplete =
    Boolean(p.lookingFor && p.lookingFor.trim()) &&
    Boolean(p.canHelpWith && p.canHelpWith.trim());
  if (!profileComplete) return `/e/${slug}/goals`;
  return `/e/${slug}/feed`;
}

export function WelcomePage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const pid = searchParams.get("pid");
  const { user, loading: userLoading } = useAuthUser();

  // Legacy magic-link from old emails: ?pid=<participantId>. Same routing
  // rules apply — incomplete → /continue, no video → /profile, ready → /feed.
  useEffect(() => {
    if (!slug || !pid) return;
    let cancelled = false;
    (async () => {
      try {
        const participant = await fetchParticipantById(pid);
        if (cancelled || !participant) return;
        saveParticipantSession(slug, {
          participantId: participant.id,
          eventId: participant.event_id,
          name: participant.name,
          email: participant.email,
        });
        navigate(
          pickReturnTarget(
            {
              contactForMatches: participant.contact_for_matches,
              lookingFor: participant.looking_for,
              canHelpWith: participant.can_help_with,
            },
            slug,
          ),
          { replace: true },
        );
      } catch {
        /* invalid pid */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, pid, navigate]);

  const { data: event, isLoading, error } = useQuery({
    queryKey: ["event-slug", slug],
    queryFn: () => fetchEventBySlug(slug ?? ""),
    enabled: Boolean(slug),
  });

  const { data: myParticipant } = useQuery({
    queryKey: ["my-participant-for-event", event?.id, user?.id],
    queryFn: () => fetchMyParticipantForEvent(event!.id),
    enabled: Boolean(event && user),
  });

  // Auth-user path: same routing rules.
  useEffect(() => {
    if (!slug || !event || !user || pid) return;
    if (myParticipant === undefined) return;
    if (myParticipant) {
      saveParticipantSession(slug, {
        participantId: myParticipant.id,
        eventId: myParticipant.event_id,
        name: myParticipant.name,
        email: myParticipant.email,
      });
      navigate(
        pickReturnTarget(
          {
            contactForMatches: myParticipant.contact_for_matches,
            lookingFor: myParticipant.looking_for,
            canHelpWith: myParticipant.can_help_with,
          },
          slug,
        ),
        { replace: true },
      );
    }
  }, [slug, event, user, myParticipant, navigate, pid]);

  // Anon participant path: no Supabase Auth user, but a localStorage
  // session from a previous visit exists. Until now they were always shown
  // the welcome+Join screen as if they were brand new — annoying for
  // people who already recorded a pitch and just want to see the feed.
  // Reuse the same routing rules by looking up the participant by id.
  useEffect(() => {
    if (!slug || pid) return;
    // Only kick in once we know there's no auth user — otherwise we'd
    // race the auth-user effect above.
    if (userLoading || user) return;
    const local = getParticipantSession(slug);
    if (!local) return;

    let cancelled = false;
    (async () => {
      try {
        const participant = await fetchParticipantById(local.participantId);
        if (cancelled || !participant) return;
        navigate(
          pickReturnTarget(
            {
              contactForMatches: participant.contact_for_matches,
              lookingFor: participant.looking_for,
              canHelpWith: participant.can_help_with,
            },
            slug,
          ),
          { replace: true },
        );
      } catch {
        // Stale session — leave the welcome screen visible so they can
        // re-register cleanly.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, pid, user, userLoading, navigate]);

  if (userLoading || isLoading) {
    return (
      <MobileFrame>
        <Loader label="Loading event..." />
      </MobileFrame>
    );
  }

  if (error || !event) {
    return (
      <MobileFrame>
        <Card padding="lg" className="m-6 text-center">
          <EmptyState
            icon="🔎"
            title="Event not found"
            description="This link is invalid or the event has ended."
          />
        </Card>
      </MobileFrame>
    );
  }

  const isAuthNotLinked = Boolean(user) && myParticipant === null;

  return (
    <MobileFrame>
      <div className="flex-1 px-4 py-6 animate-fade-in">
        <h1 className="text-[24px] font-bold tracking-tight text-[#1a1a1a]">{event.title}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-[#6c757d]">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            {formatEventDate(event.event_date)}
          </span>
          {event.location ? (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" />
              {event.location}
            </span>
          ) : null}
          <span className="inline-flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" />
            {event.participant_count ?? 0} {event.participant_count === 1 ? "participant" : "participants"}
          </span>
        </div>

        {event.description ? (
          <p className="mt-4 whitespace-pre-line break-words rounded-[12px] bg-[#f8fafc] p-4 text-[14px] leading-relaxed text-[#475569]">
            {event.description}
          </p>
        ) : null}

        <div className="mt-6 flex flex-col gap-3">
          {ONBOARDING_STEPS.map((step, i) => (
            <Card key={step.title} tone="default" padding="md">
              <div className="flex items-start gap-3">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#e6f1fb] text-[#2563eb]">
                  <step.icon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-[#94a3b8]">
                    Step {i + 1}
                  </p>
                  <h3 className="mt-0.5 text-[15px] font-bold text-[#1a1a1a]">{step.title}</h3>
                  <p className="mt-1 text-[13px] text-[#475569] leading-relaxed">{step.desc}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {isAuthNotLinked ? (
          <Button
            type="button"
            fullWidth
            className="mt-8"
            onClick={() => {
              if (slug) clearParticipantSession(slug);
              navigate(`/e/${slug}/continue`);
            }}
          >
            Join
          </Button>
        ) : (
          <Button
            type="button"
            fullWidth
            className="mt-8"
            onClick={() => {
              if (slug) clearParticipantSession(slug);
              navigate(`/e/${slug}/join`);
            }}
          >
            Join
          </Button>
        )}

        <p className="mt-4 text-center text-[12px] text-[#94a3b8]">
          By continuing you accept the{" "}
          <Link to="/rules" className="text-[#2563eb] hover:underline">
            platform rules
          </Link>
          .
        </p>
      </div>
    </MobileFrame>
  );
}
